/**
 * mutateTyped — Pillar 3 typed mutation primitive for POST/PATCH/PUT/DELETE
 * SL endpoints.
 *
 * Wraps the existing `serviceFetch` underlying primitive (via `post`,
 * `patch`, `put`, `del`) with:
 *
 *   - Request validation: caller-supplied `requestSchema` is `parse`-d on
 *     the body BEFORE the network round-trip (so invalid local state
 *     surfaces as a typed throw, never a 422 from SL).
 *
 *   - Response validation: caller-supplied `responseSchema` is
 *     `safeParse`-d on the SL response. On parse failure throws
 *     `ContractMismatchError`.
 *
 *   - Typed error mapping: on HTTP non-2xx, attempts to parse the body as
 *     `{detail: {error: {code, message}}}` (the canonical SL typed-error
 *     envelope) and throws `SLApiError` with the typed `code` enum.
 *     Non-typed error bodies degrade to `code = 'UNKNOWN'` with the HTTP
 *     status preserved.
 *
 * D-33 R1: NEVER returns silently on shape drift. Always throws.
 *
 * Per ADR-CROSS-SYSTEM-CONTRACTS-V1 v4 §2.2 Pillar 3.
 *
 * Usage:
 *   const result = await mutateTyped({
 *     method: 'POST',
 *     path: '/v1/commander/inbox/send',
 *     body: { target: 'awacs', text: 'hi' },
 *     requestSchema: SendMessageRequestSchema,
 *     responseSchema: SendMessageResponseSchema,
 *     slug: 'commander.inbox.send',
 *   })
 */

import type { z } from 'zod'
import { ServiceHttpError } from './errorEnvelope'
import { del, patch, post, put } from './serviceLayerClient'
import { ContractMismatchError, SLApiError } from './slErrors'

export type MutateMethod = 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface MutateTypedArgs<TReq, TRes> {
  method: MutateMethod
  path: string
  body?: TReq
  requestSchema?: z.ZodType<TReq>
  responseSchema: z.ZodType<TRes>
  /**
   * Optional surface tag for ContractMismatchError / SLApiError
   * forensics. Defaults to `path`.
   */
  slug?: string
  signal?: AbortSignal
  timeoutMs?: number
}

/**
 * Typed mutation. Returns the parsed response. Throws `SLApiError` on
 * HTTP non-2xx, `ContractMismatchError` on response shape drift.
 *
 * @throws `ContractMismatchError` (D-33 R1) on response safeParse failure.
 * @throws `SLApiError` on HTTP non-2xx.
 * @throws `ContractMismatchError` on request safeParse failure (local
 *         caller bug — caller passed body that does not match
 *         requestSchema).
 */
export async function mutateTyped<TReq, TRes>(
  args: MutateTypedArgs<TReq, TRes>,
): Promise<TRes> {
  const slug = args.slug ?? args.path

  // Pre-flight: validate request body against requestSchema so caller
  // bugs surface locally, not as a 422 round-trip.
  if (args.requestSchema && args.body !== undefined) {
    const reqParsed = args.requestSchema.safeParse(args.body)
    if (!reqParsed.success) {
      throw new ContractMismatchError({
        slug,
        expectedShape: 'mutateTyped<request>',
        actualShape: summarizeActualShape(args.body),
        rawData: args.body,
        message:
          'Request shape mismatch on "'
          + slug
          + '": '
          + reqParsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; '),
      })
    }
  }

  // Issue the request via the existing transport. The transport throws
  // `ServiceHttpError` on non-2xx; we translate that into `SLApiError`
  // so the typed-error surface is uniform.
  let raw: unknown
  try {
    const res = await dispatch<TReq>(args)
    raw = res.data
  } catch (e) {
    if (e instanceof ServiceHttpError) {
      throw new SLApiError({
        code: e.code,
        status: e.status ?? 0,
        message: e.message,
        slug,
        details: e.details,
      })
    }
    throw e
  }

  // Validate response shape — D-33 R1, no silent passthrough.
  const parsed = args.responseSchema.safeParse(raw)
  if (!parsed.success) {
    throw new ContractMismatchError({
      slug,
      expectedShape: 'mutateTyped<response>',
      actualShape: summarizeActualShape(raw),
      rawData: raw,
      message:
        'Response shape mismatch on "'
        + slug
        + '": '
        + parsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; '),
    })
  }

  return parsed.data
}

async function dispatch<TReq>(
  args: MutateTypedArgs<TReq, unknown>,
): Promise<{ data: unknown }> {
  const init = { signal: args.signal, timeoutMs: args.timeoutMs }
  switch (args.method) {
    case 'POST':
      return post<unknown>(args.path, args.body, init)
    case 'PATCH':
      return patch<unknown>(args.path, args.body, init)
    case 'PUT':
      return put<unknown>(args.path, args.body, init)
    case 'DELETE':
      return del<unknown>(args.path, init)
  }
}

function summarizeActualShape(value: unknown): string {
  if (value === null) return 'null'
  if (typeof value !== 'object') return typeof value
  if (Array.isArray(value)) return 'array(' + value.length + ')'
  const keys = Object.keys(value as Record<string, unknown>)
  return 'object{' + keys.slice(0, 6).join(',') + (keys.length > 6 ? ',…' : '') + '}'
}
